import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Clock, 
  Sparkles, 
  Wand2, 
  Layers3, 
  Video, 
  BarChart3, 
  Brain,
  Calendar,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/layout/Navbar';
import { Link } from 'react-router-dom';

const roadmapPhases = [
  {
    phase: 'Phase 1',
    title: 'Foundation Launch',
    status: 'available',
    timeline: 'Available Now',
    description: 'Core AI-powered content creation capabilities to get you started with professional social media content.',
    features: [
      {
        name: 'Image Studio',
        description: 'AI-powered image generation and enhancement with multiple style variations',
        icon: Wand2,
        highlights: ['Multiple Style Generation', 'Composition Enhancement', 'Brand Kit Integration', 'Multi-Product Creation']
      },
      {
        name: '3D & AR Studios',
        description: 'Create immersive 3D models and AR experiences from product images',
        icon: Layers3,
        highlights: ['3D Model Generation', 'AR Ad Creation', 'Interactive Previews', 'Cross-Platform AR']
      },
      {
        name: 'Social Connections & Analytics',
        description: 'Connect all social platforms with comprehensive analytics and insights',
        icon: BarChart3,
        highlights: ['Multi-Platform Publishing', 'Advanced Analytics', 'Social Health Score', 'Audience Insights']
      },
      {
        name: 'Post scheduling & publishing',
        description: 'Schedule and Publish posts to across social media platforms.',
        icon: CheckCircle,
        highlights: ['Smart Captions and Tags', 'One-Click Creator: Our streamlined Post creation flow', 'Schedule post publishing']
      }
    ]
  },
  {
    phase: 'Phase 2',
    title: 'Video Content Expansion',
    status: 'development',
    timeline: 'Q1 2026',
    description: 'Expand into video content creation with AI-powered video generation and editing capabilities.',
    features: [
      {
        name: 'Ad Studio Video',
        description: 'AI-powered video generation, editing, and dynamic content creation',
        icon: Video,
        highlights: ['AI Video Generation', 'Dynamic Video content', 'Smart Editing Tools', 'Multi-Format Export']
      }
    ]
  },
  {
    phase: 'Phase 3',
    title: 'Predictive Intelligence',
    status: 'research',
    timeline: 'Q2 2026',
    description: 'Advanced AI insights and predictive analytics to optimize content performance and conversion.',
    features: [
      {
        name: 'Smart Content Analysis & Insights',
        description: 'AI-powered content analysis with predictive features for maximum conversion',
        icon: Brain,
        highlights: ['Predictive Performance Analysis', 'Content Optimization Suggestions', 'Conversion Prediction', 'Smart A/B Testing']
      }
    ]
  }
];

const statusConfig = {
  available: {
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgColor: 'bg-green-50',
    label: 'Available Now'
  },
  development: {
    color: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgColor: 'bg-blue-50',
    label: 'In Development'
  },
  planned: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    label: 'Planned'
  },
  research: {
    color: 'bg-purple-500',
    textColor: 'text-purple-700',
    bgColor: 'bg-purple-50',
    label: 'Research Phase'
  }
};

const RoadmapPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Navbar />
      
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}>
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold bg-clip-text text-transparent" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Product Roadmap
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our journey to revolutionize social media content creation with AI-powered tools and intelligent insights.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Roadmap Timeline */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 hidden lg:block" style={{ background: 'linear-gradient(to bottom, #30b3dd, #4a298a)' }}></div>
          
          <div className="space-y-12">
            {roadmapPhases.map((phase, index) => {
              const config = statusConfig[phase.status];
              return (
                <motion.div
                  key={phase.phase}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="relative"
                >
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-4 h-4 rounded-full bg-white border-4 hidden lg:block" style={{ borderColor: '#30b3dd' }}></div>
                  
                  <div className="lg:ml-20">
                    <Card className="bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
                      <CardHeader>
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div>
                            <div className="flex items-center space-x-3 mb-2">
                              <Badge variant="outline" className="text-sm font-medium">
                                {phase.phase}
                              </Badge>
                              <Badge className={`${config.bgColor} ${config.textColor} border-0`}>
                                {config.label}
                              </Badge>
                            </div>
                            <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                              {phase.title}
                            </CardTitle>
                            <div className="flex items-center space-x-2 text-gray-600 mb-3">
                              <Clock className="w-4 h-4" />
                              <span className="font-medium">{phase.timeline}</span>
                            </div>
                          </div>
                          <div className={`w-3 h-3 ${config.color} rounded-full`}></div>
                        </div>
                        <CardDescription className="text-gray-600 text-base leading-relaxed">
                          {phase.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          {phase.features.map((feature, featureIndex) => {
                            const Icon = feature.icon;
                            return (
                              <div key={feature.name} className="border border-gray-200 rounded-lg p-4 bg-white/50">
                                <div className="flex items-start space-x-4">
                                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}>
                                    <Icon className="w-5 h-5 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                      {feature.name}
                                    </h4>
                                    <p className="text-gray-600 mb-3">
                                      {feature.description}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {feature.highlights.map((highlight, highlightIndex) => (
                                        <span
                                          key={highlightIndex}
                                          className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full"
                                        >
                                          <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                                          {highlight}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          className="text-center mt-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <div className="rounded-2xl p-8 text-white" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}>
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Sparkles className="w-8 h-8" />
              <h2 className="text-2xl lg:text-3xl font-bold">
                Start Your Journey Today
              </h2>
            </div>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto text-lg">
              Begin with our core content creation tools and grow with us as we expand our platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white hover:bg-gray-100 font-semibold" style={{ color: '#4a298a' }}>
                  Start Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Feedback Section */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          <Card className="bg-white/70 backdrop-blur-sm border border-gray-200/60 max-w-2xl mx-auto">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Shape Our Future
              </h3>
              <p className="text-gray-600 mb-4">
                Have ideas for features you'd like to see? We'd love to hear from you and incorporate your feedback into our roadmap.
              </p>
              <Link to="/contact">
                <Button variant="outline" className="font-medium">
                  Share Your Ideas
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default RoadmapPage;